import { isImmutable, List, Map } from 'immutable';

import { shapeFoldersForApp, shapeImagesForApp, shapeVideosForApp } from '../utils/studio/shapeForApp';
import { createController } from '../utils/controller';
import { getFilters } from '../utils/studio/filters';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { IMAGES_PER_PAGE, MEDIA_TYPE, ORDER_BY } from '../constants/studio';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { EMPTY } from '../constants';

import actions from '../actions';

const imagesController = createController();
const foldersController = createController();

const MEDIA_API = {
  [MEDIA_TYPE.PHOTOS]: api.studio.photos,
  [MEDIA_TYPE.VIDEOS]: api.studio.videos,
};

function shapeMediaResponseForApp({ items, mediaType, response }) {
  switch (mediaType) {
    case MEDIA_TYPE.PHOTOS: {
      return response.images.reduce(shapeImagesForApp, items);
    }

    case MEDIA_TYPE.VIDEOS: {
      return response.videos.reduce(shapeVideosForApp, items);
    }

    default: {
      return items;
    }
  }
}

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    imagesController,
    foldersController,
  ].forEach(abort);
}

function getShopParams(state) {
  const channel = state.getIn(['selectMedia', 'channel']);
  const db = state.getIn(['selectMedia', 'db']);
  const shopId = state.getIn(['selectMedia', 'shopId']);
  const mediaType = state.getIn(['selectMedia', 'mediaType']);
  return { channel, db, shopId, mediaType };
}

function* bootstrap(state, { channel, db, mediaType, shopId }) {
  abortAll();

  yield function effect(dispatch) {
    dispatch(actions.selectMedia.getFolders());
    dispatch(actions.selectMedia.getItems());
  };

  return state.set('selectMedia', Map({ channel, db, mediaType, shopId }));
}

function* cleanUp(state) {
  abortAll();
  return state.delete('selectMedia');
}

function* getFolders(state) {
  if (!state.has('selectMedia')) return state;

  const { db, mediaType, shopId } = getShopParams(state);
  const { signal } = foldersController.start();

  yield function effect(dispatch) {
    MEDIA_API[mediaType].getFolders({ db, shopId, signal })
      .then(
        (response) => dispatch(actions.selectMedia.getFolders.fulfilled({ mediaType, response })),
        (error) => dispatch(actions.selectMedia.getFolders.rejected({ error, signal })),
      );
  };

  return state;
}

function* getFoldersFulfilled(state, { mediaType, response }) {
  if (!state.has('selectMedia')) return state;

  abort(foldersController);

  let selectMedia = state.get('selectMedia');

  const allItems = Map({
    count: parseInt(response.image_count) || parseInt(response.video_count) || 0,
    name:`All ${mediaType}`,
  });

  const folders = response.folders.reduce(
    shapeFoldersForApp,
    selectMedia
      .get('folders', Map({ selected: null }))
      .set('byId', EMPTY.MAP.set(null, allItems))
      .set('ids', List([null]))
  );

  selectMedia = selectMedia
    .set('folders', folders)
    .set('loaded', selectMedia.has('items'));

  if (selectMedia.get('loaded') && !selectMedia.getIn(['request', 'folder'])) {
    selectMedia = selectMedia.setIn(['request', 'folder'], folders.getIn(['byId', folders.get('selected')]));
  }

  return state.set('selectMedia', selectMedia);
}

function* getFoldersRejected(state, { error, signal }) {
  if (signal.aborted || !state.has('selectMedia')) return state;

  abort(foldersController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return state
    .setIn(['selectMedia', 'folders'], Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .setIn(['selectMedia', 'loaded'], state.hasIn(['selectMedia', 'items']));
}

function* getItems(state, payload = {}) {
  if (!state.has('selectMedia')) return state;

  const { signal } = imagesController.start();
  const { db, shopId, mediaType } = getShopParams(state);
  let selectMedia = state.get('selectMedia').setIn(['request', 'processing'], true);
  const { folder = selectMedia.getIn(['request', 'folder', 'uuid'], null) } = payload;
  let {
    clear = false,
    page = selectMedia.getIn(['request', 'page'], 0),
    query = selectMedia.getIn(['request', 'query'], ''),
  } = payload;

  if (selectMedia.has('folders') && folder !== selectMedia.getIn(['folders', 'selected'])) {
    selectMedia = selectMedia.setIn(['folders', 'selected'], folder);
  }

  if (folder !== selectMedia.getIn(['request', 'folder', 'uuid'], null)) {
    clear = true;
    query = '';
  } else if (query !== selectMedia.getIn(['request', 'query'], '')) {
    clear = true;
    selectMedia = selectMedia.set('query', query);
  }

  if (clear) {
    selectMedia = selectMedia.delete('lastInteraction').setIn(['items', 'loading'], true);
    page = 0;
  }

  const order = { by: ORDER_BY.CONNECTED_LISTINGS, type: false };
  const request = Map({
    folder: selectMedia.getIn(['folders', 'byId', folder]),
    order: Map(order),
    page,
    query,
  });

  yield function effect(dispatch) {
    MEDIA_API[mediaType]
      .getItems({ db, filters: getFilters({ page, query, order }), folder, shopId, signal, query })
      .then(
        (response) => dispatch(actions.selectMedia.getItems.fulfilled({ clear, mediaType, request, response })),
        (error) => dispatch(actions.selectMedia.getItems.rejected({ error, request, signal })),
      );
  };

  return state.set('selectMedia', selectMedia);
}

function* getItemsFulfilled(state, { clear, mediaType, request, response }) {
  if (!state.has('selectMedia')) return state;

  let selectMedia = state.get('selectMedia');
  let items = selectMedia
    .get('items', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .delete('loading')
    .delete('processing')
    .set('total', parseInt(response.count));

  if (clear) {
    items = items
      .delete('selected')
      .set('byId', EMPTY.MAP)
      .set('ids', EMPTY.LIST);
  }

  selectMedia = selectMedia
    .set('items', shapeMediaResponseForApp({ items, mediaType, response }))
    .set('loaded', selectMedia.has('folders'))
    .set('request', request);

  if ((request.get('page') + 1) * IMAGES_PER_PAGE < items.get('total')) {
    selectMedia = selectMedia.setIn(['request', 'hasMore'], true);
  }

  if (selectMedia.get('loaded') && !request.get('folder') && getSize(selectMedia.getIn(['folders', 'byId'])) > 0) {
    selectMedia = selectMedia.setIn(['request', 'folder'],
      selectMedia.getIn(['folders', 'byId', selectMedia.getIn(['folders', 'selected'], null)])
    );
  }

  abort(imagesController);

  return state.set('selectMedia', selectMedia);
}

function* getItemsRejected(state, { error, request, signal }) {
  if (signal.aborted || !state.has('selectMedia')) return state;

  abort(imagesController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  const selectMedia = state
    .get('selectMedia')
    .set('items', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST, total: 0 }))
    .set('request', request);

  return state.set('selectMedia', selectMedia.set('loaded', selectMedia.has('folders')));
}

function* setValue(state, { path, value }) {
  if (!path || !state.has('selectMedia')) return state;

  const fullPath = Array.isArray(path)
    ? ['selectMedia', ...path]
    : ['selectMedia', path];

  return value === undefined
    ? state.deleteIn(fullPath)
    : state.setIn(fullPath, value);
}

function* toggleItemState(state, { items, state: path, value }) {
  if (!state.has('selectMedia')) return state;

  function reduceImageIds(result, uuid) {
    switch (value) {
      case true: {
        return result.add(uuid);
      }

      case false: {
        return result.remove(uuid);
      }

      default: {
        return result.has(uuid)
          ? result.remove(uuid)
          : result.add(uuid);
      }
    }
  }

  const fullPath = ['selectMedia', 'items', path];
  const itemsState = typeof items === 'string'
    ? reduceImageIds(state.getIn(fullPath, EMPTY.SET), items)
    : Array.isArray(items) || isImmutable(items)
      ? items.reduce(reduceImageIds, state.getIn(fullPath, EMPTY.SET))
      : null;

  return getSize(itemsState) > 0
    ? state.setIn(fullPath, itemsState)
    : state.deleteIn(fullPath);
}

const selectMedia = {
  [actions.selectMedia.bootstrap]: bootstrap,
  [actions.selectMedia.cleanUp]: cleanUp,
  [actions.selectMedia.getFolders]: getFolders,
  [actions.selectMedia.getFolders.fulfilled]: getFoldersFulfilled,
  [actions.selectMedia.getFolders.rejected]: getFoldersRejected,
  [actions.selectMedia.getItems]: getItems,
  [actions.selectMedia.getItems.fulfilled]: getItemsFulfilled,
  [actions.selectMedia.getItems.rejected]: getItemsRejected,
  [actions.selectMedia.setValue]: setValue,
  [actions.selectMedia.toggleItemState]: toggleItemState,
};

export default selectMedia;
